import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Activity, Loader2 } from 'lucide-react';
import { authStorage } from '@/utils/authStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;

function Bucket({ title, hint, data, testid }) {
  const pct = (n) => (data.limit < 0 ? 0 : Math.min(100, Math.round((n / Math.max(1, data.limit)) * 100)));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</p>
        <p className="text-[12px] font-mono">
          {data.used}{data.limit < 0 ? ' · unlimited' : ` / ${data.limit}`}
        </p>
      </div>
      {data.items.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">Nothing used yet this month.</p>
      ) : (
        <ul className="space-y-1.5">
          {data.items.map((it) => (
            <li key={it.kind} className="text-[13px]" data-testid={`usage-item-${it.kind}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate">{it.label}</span>
                <span className="font-mono text-[12px] text-muted-foreground shrink-0">
                  {it.count}{data.limit > 0 ? ` · ${pct(it.count)}%` : ''}
                </span>
              </div>
              <div className="h-1 mt-1 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-[#1d4ed8]"
                  style={{ width: `${data.limit < 0 ? Math.min(100, it.count) : pct(it.count)}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-muted-foreground mt-2">{hint}</p>
    </div>
  );
}

/** "Usage this month" — which module is eating the quota. */
export default function UsageBreakdownCard() {
  const userId = authStorage.getAuthorUser()?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/usage`, { params: { user_id: userId } })
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <Card data-testid="usage-breakdown-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#1d4ed8]" /> Usage this month
          {data?.month && <span className="text-[11px] font-mono text-muted-foreground">{data.month}</span>}
        </CardTitle>
        <CardDescription>
          Heavy runs count against your generations; inline helpers (ghost text, toolbar, Copilot)
          count against assists.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Usage is not available right now.</p>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-8">
              <Bucket title="AI generations" testid="usage-generations"
                hint="Sections, whole articles, Premium engine, Defence, Readiness, Wave-D, grounding, Python project."
                data={data.generations} />
              <Bucket title="Inline AI assists" testid="usage-assists"
                hint="Continue / ghost text, selection toolbar, field generation, Copilot messages, live claim checks."
                data={data.assists} />
            </div>
            {data.recent?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-border" data-testid="usage-recent">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                  Last 10 actions
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {data.recent.map((r, i) => (
                    <li key={i} className="text-[12px] flex justify-between gap-3">
                      <span className="truncate">{r.label}</span>
                      <span className="font-mono text-muted-foreground shrink-0">
                        {String(r.at || '').slice(0, 16).replace('T', ' ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
